import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Activity,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Gauge,
  Wifi,
  WifiOff,
  Thermometer,
  Waves,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getWebSocketClient } from '../../utils/websocket-client';
import { useTheme } from '../../contexts/ThemeContext';
import { adminApi } from '../../utils/admin-api';
import { generateCustomPLCReport } from '../../utils/advanced-pdf-generator';
import { API_BASE } from '../../utils/api-base';
import { vfdApi, type VfdDrive } from '../../utils/vfd';
import { VFDDrivesPanel } from './VFDDrivesPanel';
import { ControllersPanel } from './ControllersPanel';
import { controllerApi, type ProcessController } from '../../utils/controllers';

interface LivePLCDataProps {
  client?: any;
  clientId: string;
  token: string;
  permissions: any;
}

type PlcRuntimeStatus = {
  connected: boolean;
  reconnecting?: boolean;
  error?: string | null;
};

// Tags don't have an explicit "category" field — only a configured unit (set when the
// tag was added). This infers a measurement category from that real unit string so tags
// can be grouped/themed like Temperature, Flow Rate, Pressure, etc. Anything unmatched
// falls back to a neutral "Other" group rather than guessing.
const CATEGORY_RULES: { test: RegExp; label: string; icon: any; border: string; iconWrap: string; accent: string }[] = [
  { test: /^(°?c|°?f|degc|degf|deg\s?c|deg\s?f|celsius|fahrenheit)$/, label: 'Temperature', icon: Thermometer, border: 'border-t-red-500', iconWrap: 'bg-red-500/10 text-red-400', accent: '#ef4444' },
  { test: /^(m3\/h|m³\/h|l\/min|lpm|gpm|cfm|l\/s|m3\/s|m³\/s)$/, label: 'Flow Rate', icon: Waves, border: 'border-t-blue-500', iconWrap: 'bg-blue-500/10 text-blue-400', accent: '#3b82f6' },
  { test: /^(bar|psi|kpa|mpa|pa)$/, label: 'Pressure', icon: Gauge, border: 'border-t-purple-500', iconWrap: 'bg-purple-500/10 text-purple-400', accent: '#a855f7' },
  { test: /^(v|kv|volt|volts)$/, label: 'Voltage', icon: Zap, border: 'border-t-amber-500', iconWrap: 'bg-amber-500/10 text-amber-400', accent: '#f59e0b' },
  { test: /^(a|ma|amp|amps)$/, label: 'Current', icon: Zap, border: 'border-t-yellow-500', iconWrap: 'bg-yellow-500/10 text-yellow-400', accent: '#eab308' },
  { test: /^(kw|w|watt|watts|hp)$/, label: 'Power', icon: Zap, border: 'border-t-orange-500', iconWrap: 'bg-orange-500/10 text-orange-400', accent: '#f97316' },
  { test: /^(kwh|wh|mwh)$/, label: 'Energy', icon: Activity, border: 'border-t-teal-500', iconWrap: 'bg-teal-500/10 text-teal-400', accent: '#14b8a6' },
  { test: /^(%|percent)$/, label: 'Level', icon: Activity, border: 'border-t-cyan-500', iconWrap: 'bg-cyan-500/10 text-cyan-400', accent: '#06b6d4' },
  { test: /^rpm$/, label: 'Speed', icon: Activity, border: 'border-t-pink-500', iconWrap: 'bg-pink-500/10 text-pink-400', accent: '#ec4899' },
];
const DEFAULT_CATEGORY = { label: 'Other', icon: Activity, border: 'border-t-slate-500', iconWrap: 'bg-slate-500/10 text-slate-400', accent: '#64748b' };

// Validated categorical palette (fixed order, never cycled per-render) — used to tell
// apart multiple tags plotted within the same per-category trend chart.
const CHART_PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const categorizeUnit = (unit: string) => {
  const u = String(unit || '').trim().toLowerCase();
  const rule = CATEGORY_RULES.find((r) => r.test.test(u));
  return rule || DEFAULT_CATEGORY;
};

const MiniSparkline = ({ data, color }: { data: number[]; color: string }) => {
  if (!data || data.length < 2) {
    return <div className="h-8" />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 100;
  const h = 32;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

type TrendRange = 'live' | 'day' | 'week' | 'month';

// Computes the [start, end) window for a given range + how many periods back
// ("offset") to look, along with a sensible aggregation bucket size (matches the
// interval param the existing /historical-data endpoint already accepts) and a
// human label for the toolbar.
const computeTrendWindow = (range: Exclude<TrendRange, 'live'>, offset: number) => {
  const now = new Date();
  if (range === 'day') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - offset);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    const label = offset === 0 ? 'Today' : offset === 1 ? 'Yesterday' : start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return { start, end, intervalMinutes: 5, label };
  }
  if (range === 'week') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay() - offset * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    const lastDay = new Date(end.getTime() - 86400000);
    const label = `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${lastDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    return { start, end, intervalMinutes: 60, label };
  }
  const start = new Date(now.getFullYear(), now.getMonth() - offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - offset + 1, 1);
  const label = start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  return { start, end, intervalMinutes: 360, label };
};

function LivePLCTagsView({ client, clientId, token, permissions }: LivePLCDataProps) {
  const isDark = useTheme().theme === 'dark';
  const [liveData, setLiveData] = useState<any[]>([]);
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [plcs, setPlcs] = useState<any[]>([]);
  const [selectedPlcId, setSelectedPlcId] = useState<string | null>(null);
  const [plcConnectionStatus, setPlcConnectionStatus] = useState<Record<string, PlcRuntimeStatus>>({});
  const [showCharts, setShowCharts] = useState(false);
  // A simulated client has no real hardware — any bridge connection attempt against
  // its configured (often placeholder) PLC IP is expected to fail and is irrelevant
  // noise, not a real problem. Suppress hardware connection error/reconnect toasts
  // and status for these clients; simulated live data already marks them "connected".
  const isSimulationMode = !!(client?.simulationMode ?? client?.simulation_mode);
  // Min/Max threshold coloring on tiles is tied to the same per-client "Smart Alerts"
  // module toggle the backend alarm engine already gates on, so a client without
  // that access enabled doesn't see threshold breaches highlighted either.
  const smartAlertsEnabled = client?.modules?.smartAlerts === true;
  const [realtimeControlAllowed, setRealtimeControlAllowed] = useState<boolean | null>(null);
  const [sectionsByPlc, setSectionsByPlc] = useState<Record<string, any[]>>({});
  const [selectedSectionByPlc, setSelectedSectionByPlc] = useState<Record<string, string>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [trendRange, setTrendRange] = useState<TrendRange>('live');
  const [trendOffset, setTrendOffset] = useState(0);
  const [historicalTrendData, setHistoricalTrendData] = useState<any[]>([]);
  const [historicalTrendLoading, setHistoricalTrendLoading] = useState(false);
  const tagHistoryRef = useRef<Record<string, number[]>>({});
  const pollingIntervalMs = client?.pollingIntervalMs;
  const effectivePollingIntervalMs = Math.max(1000, Number(pollingIntervalMs) || 5000);
  const wsClient = useRef<any>(null);
  const historicalBuffer = useRef<any[]>([]);
  const liveDataBuffer = useRef<Record<string, any>>({});
  const lastRealtimePacketAt = useRef<number>(0);
  const lastUpdateTime = useRef<number>(0);
  const sectionLoadKeyRef = useRef<string>('');
  const realtimeFreshnessThresholdMs = effectivePollingIntervalMs * 4;
  // Was effectivePollingIntervalMs * 60 (up to 5 hours) back when this mode's only
  // data source was the 5-minute REST snapshot poll and a wide margin avoided false
  // "stale" flicker between cycles. Now that the bridge's actual per-second reads are
  // broadcast live over the SSE relay independent of that (unchanged) save interval,
  // a fixed short window gives the "instant" disconnect detection that's now possible
  // instead of inheriting a threshold sized for the old, much slower path.
  const syncFreshnessThresholdMs = 20000;
  const TAG_CORRELATION_WINDOW_MS = 15 * 60 * 1000;
  const MAX_HISTORY_POINTS = Math.max(1200, Math.ceil(TAG_CORRELATION_WINDOW_MS / 1000) + 120);
  const getPlcConfigValue = (plc: any, camel: string, snake: string, fallback: any = null) =>
    plc?.[camel] ?? plc?.[snake] ?? fallback;

  const tagCorrelationData = useMemo(() => {
    const cutoffTs = Date.now() - TAG_CORRELATION_WINDOW_MS;
    return historicalData.filter((point) => Number(point?.timestamp) >= cutoffTs);
  }, [historicalData]);

  // Reset back to the current period whenever the range type changes (switching
  // from Day to Week shouldn't carry over "3 periods back").
  useEffect(() => {
    setTrendOffset(0);
  }, [trendRange]);

  // Real historical data for Day/Week/Month trend views — same aggregation
  // endpoint the Reports page uses, scoped to the selected PLC. "Live" mode
  // keeps using the rolling in-memory buffer above instead of hitting the DB.
  useEffect(() => {
    if (trendRange === 'live' || !selectedPlcId) {
      setHistoricalTrendData([]);
      return;
    }
    let cancelled = false;
    const { start, end, intervalMinutes } = computeTrendWindow(trendRange, trendOffset);
    // The endpoint applies LIMIT to raw rows *before* aggregating into buckets, so a
    // too-small limit silently drops the earlier part of the range (only the most
    // recent N raw rows survive) — a full month across many tags at a 5-min persist
    // cadence can be well over 100k raw rows. Size the limit to the range, not a flat 5000.
    const rawRowLimit = trendRange === 'day' ? 50000 : trendRange === 'week' ? 150000 : 400000;
    setHistoricalTrendLoading(true);
    adminApi.getHistoricalData(token, clientId, {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      plcId: selectedPlcId,
      interval: intervalMinutes,
      limit: rawRowLimit,
    }).then((result: any) => {
      if (cancelled) return;
      const rows = Array.isArray(result?.data) ? result.data : [];
      const bucketMs = intervalMinutes * 60 * 1000;
      const bucketMap = new Map<number, any>();
      for (const row of rows) {
        const ts = new Date(row.timestamp).getTime();
        if (!Number.isFinite(ts)) continue;
        const bucket = Math.floor(ts / bucketMs) * bucketMs;
        if (!bucketMap.has(bucket)) {
          bucketMap.set(bucket, {
            time: trendRange === 'day'
              ? new Date(bucket).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
              : new Date(bucket).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' }),
            timestamp: bucket,
          });
        }
        const val = Number(row.value);
        if (Number.isFinite(val)) {
          bucketMap.get(bucket)[`${selectedPlcId}_${row.tag_name}`] = val;
        }
      }
      setHistoricalTrendData(Array.from(bucketMap.values()).sort((a, b) => a.timestamp - b.timestamp));
    }).catch(() => {
      if (!cancelled) setHistoricalTrendData([]);
    }).finally(() => {
      if (!cancelled) setHistoricalTrendLoading(false);
    });
    return () => { cancelled = true; };
  }, [trendRange, trendOffset, selectedPlcId, clientId, token]);

  // Backfill a real 15-minute history for whichever PLC is selected, right away.
  // The one-time global seed further down fetches the most recent 1000 rows across
  // the WHOLE client, unscoped by PLC — with several PLCs and many tags competing
  // for that budget, the currently-viewed PLC can end up with only a handful of
  // points, so "Live" looked like it only covered a few seconds instead of 15
  // minutes. This fetches specifically for the selected PLC and merges it in.
  useEffect(() => {
    if (!selectedPlcId || !clientId || !token) return;
    let cancelled = false;
    const end = new Date();
    const start = new Date(end.getTime() - TAG_CORRELATION_WINDOW_MS);
    adminApi.getHistoricalData(token, clientId, {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      plcId: selectedPlcId,
      limit: 20000,
    }).then((result: any) => {
      if (cancelled) return;
      const rows = Array.isArray(result?.data) ? result.data : [];
      if (rows.length === 0) return;
      const bucketMap = new Map<number, any>();
      for (const existing of historicalBuffer.current) {
        bucketMap.set(existing.timestamp, existing);
      }
      for (const row of rows) {
        const ts = new Date(row.timestamp).getTime();
        if (!Number.isFinite(ts)) continue;
        const bucket = Math.floor(ts / 30000) * 30000;
        if (!bucketMap.has(bucket)) bucketMap.set(bucket, { time: new Date(bucket).toLocaleTimeString(), timestamp: bucket });
        const val = Number(row.value);
        if (Number.isFinite(val)) bucketMap.get(bucket)[`${selectedPlcId}_${row.tag_name}`] = val;
      }
      const merged = Array.from(bucketMap.values()).sort((a, b) => a.timestamp - b.timestamp).slice(-MAX_HISTORY_POINTS);
      historicalBuffer.current = merged;
      setHistoricalData(merged);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlcId, clientId, token]);

  const activeTrendData = trendRange === 'live' ? tagCorrelationData : historicalTrendData;
  const trendWindowLabel = trendRange === 'live' ? 'Live (last 15 min)' : computeTrendWindow(trendRange, trendOffset).label;

  // Rolling per-tag value buffer for the mini sparklines on each tile — small, capped,
  // updated whenever the throttled live-data state changes.
  useEffect(() => {
    liveData.forEach((plc: any) => {
      const plcId = String(plc?.plcId || plc?.id || '');
      const values = plc?.values || {};
      Object.entries(values).forEach(([key, value]) => {
        if (typeof value !== 'number') return;
        const histKey = `${plcId}::${key}`;
        const arr = tagHistoryRef.current[histKey] || [];
        arr.push(value);
        if (arr.length > 24) arr.shift();
        tagHistoryRef.current[histKey] = arr;
      });
    });
  }, [liveData]);

  // Throttled UI update effect
  useEffect(() => {
    const updateInterval = setInterval(() => {
      if (Object.keys(liveDataBuffer.current).length === 0) return;

      // Snapshot and clear the buffer synchronously before any setState call.
      // React (concurrent mode) may invoke the state updater later, after the
      // ref has been cleared — so we must close over the snapshot, not the ref.
      const snapshot = liveDataBuffer.current;
      liveDataBuffer.current = {};

      setLiveData((prev) => {
        const next = [...prev];
        Object.keys(snapshot).forEach((plcId) => {
          const data = snapshot[plcId];
          if (!data) return;
          const existingIndex = next.findIndex((p) => p?.plcId === plcId);
          if (existingIndex >= 0) {
            const existingTsMs = new Date(next[existingIndex]?.timestamp || '').getTime();
            const incomingTsMs = new Date(data.timestamp || '').getTime();
            if (Number.isFinite(existingTsMs) && Number.isFinite(incomingTsMs) && incomingTsMs < existingTsMs) {
              return;
            }
          }

          // Merge incoming values on top of the previous ones.
          // Tags absent from this poll cycle (read error / partial response) keep
          // their last known value instead of jumping to null / "No value".
          const prevValues = existingIndex >= 0 ? (next[existingIndex]?.values || {}) : {};
          const mergedValues: Record<string, any> = { ...prevValues };
          Object.entries(data.values || {}).forEach(([k, v]) => {
            if (v !== null && v !== undefined) {
              mergedValues[k] = v;
            }
            // null/undefined from bridge = read failed this cycle → keep prev value
          });

          const newData = {
            plcId: data.plcId,
            plcName: data.plcName,
            timestamp: data.timestamp,
            values: mergedValues,
            connectionType: data.connectionType,
            ipAddress: data.ipAddress,
            serialPort: data.serialPort,
          };

          if (existingIndex >= 0) {
            next[existingIndex] = newData;
          } else {
            next.push(newData);
          }
        });
        return next;
      });

      // Update Historical Data
      setHistoricalData([...historicalBuffer.current]);
    }, 1000); // Update UI at most once per second

    return () => clearInterval(updateInterval);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });
    wsClient.current = ws;

    ws.canUseRealtimeControl()
      .then((allowed: boolean) => {
        if (!isMounted) return;
        // In VPS/SSE mode allowed=false means we can't send commands, but we can still
        // receive live data via the portal SSE relay — so always proceed.
        setRealtimeControlAllowed(allowed ?? false);
      })
      .catch(() => {
        if (!isMounted) return;
        setRealtimeControlAllowed(true);
      });

    return () => {
      isMounted = false;
    };
  }, [clientId, token, pollingIntervalMs]);

  useEffect(() => {
    wsClient.current = getWebSocketClient(token, { clientId, pollingIntervalMs });

    // null = still loading the check; wait for it before proceeding.
    // false = VPS/SSE mode: commands won't work but live data still flows via SSE relay.
    if (realtimeControlAllowed === null) {
      return () => {};
    }

    const ws = wsClient.current;
    let isMounted = true;

    const toNumber = (value: any, fallback: number) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const normalizeTagDataType = (tag: any) => {
      return String(tag?.dataType || tag?.data_type || 'holding').trim().toLowerCase() || 'holding';
    };

    const normalizeRegisterType = (tag: any) => {
      const type = normalizeTagDataType(tag);
      if (type === 'input' || type === 'input-register' || type === 'input_register') return 'input';
      if (type === 'coil' || type === 'bool' || type === 'boolean') return 'coil';
      if (type === 'discrete' || type === 'discrete-input' || type === 'discrete_input') return 'discrete';
      return 'holding';
    };

    const loadAndSubscribePlcs = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load PLC configurations');
        }

        const allPlcs = Array.isArray(payload) ? payload : payload?.plcs || [];
        const clientPlcs = allPlcs.filter((p: any) => p.connectionType !== 'energy-meter' && p.connectionType !== 'scanner');
        if (!isMounted) return;

        setPlcs(clientPlcs);

        for (const plc of clientPlcs) {
          if (!Array.isArray(plc.tags) || plc.tags.length === 0) {
            console.warn(`Skipping PLC "${plc.name}" - no tags configured`);
            continue;
          }

          const normalizedTags = plc.tags.map((tag: any, index: number) => {
            const dataType = normalizeTagDataType(tag);
            const registerType = normalizeRegisterType(tag);
            const defaultLength = dataType === 'float' || dataType === 'float32' || dataType === 'real' ? 2 : 1;
            return {
              id: tag.id,
              name: tag.name || `tag_${index + 1}`,
              address: tag.address ?? 0,
              dataType,
              registerType,
              length: toNumber(tag.length, defaultLength),
            };
          });

          console.log(`Connecting PLC: ${plc.name} with ${normalizedTags.length} tags`);
          // In VPS/SSE mode the bridge auto-starts all devices; skip sending connectPLC
          // (it would silently fail since the direct WebSocket is disabled on VPS).
          if (realtimeControlAllowed !== true) continue;
          ws.connectPLC({
            id: plc.id,
            name: plc.name,
            connectionType: getPlcConfigValue(plc, 'connectionType', 'connection_type', 'tcp'),
            ipAddress: getPlcConfigValue(plc, 'ipAddress', 'ip_address', ''),
            port: toNumber(getPlcConfigValue(plc, 'port', 'port', 502), 502),
            serialPort: getPlcConfigValue(plc, 'serialPort', 'serial_port', ''),
            baudRate: toNumber(getPlcConfigValue(plc, 'baudRate', 'baud_rate', 9600), 9600),
            parity: plc.parity || 'none',
            dataBits: toNumber(getPlcConfigValue(plc, 'dataBits', 'data_bits', 8), 8),
            stopBits: toNumber(getPlcConfigValue(plc, 'stopBits', 'stop_bits', 1), 1),
            slaveId: toNumber(getPlcConfigValue(plc, 'slaveId', 'slave_id', 1), 1),
            tags: normalizedTags,
          });
        }
      } catch (error) {
        console.error('Failed to load PLCs:', error);
        if (isMounted) {
          toast.error(error instanceof Error ? error.message : 'Failed to load PLC configurations');
        }
      }
    };

    const connectedHandler = async () => {
      if (!isMounted) return;
      setWsConnected(true);
      ws.getStatus();
      await loadAndSubscribePlcs();
    };

    const liveDataHandler = (data: any) => {
      // Skip energy meter / scanner live data — those belong to their own dedicated pages
      if (data?.connectionType === 'energy-meter' || data?.connectionType === 'scanner') return;

      lastRealtimePacketAt.current = Date.now();
      setLoading(false);

      setPlcConnectionStatus((prev) => {
        const wasConnected = prev[data.plcId]?.connected;
        if (wasConnected === false) {
          toast.success(`${data.plcName} connected`, { id: `plc-status-${data.plcId}`, duration: 3000 });
        }
        if (prev[data.plcId]?.connected === true && prev[data.plcId]?.reconnecting === false) {
          return prev; // No change
        }
        return {
          ...prev,
          [data.plcId]: {
            connected: true,
            reconnecting: false,
            error: null,
          },
        };
      });

      // Buffer live data instead of setting state directly
      liveDataBuffer.current[data.plcId] = data;

      const previousPoint = historicalBuffer.current[historicalBuffer.current.length - 1] || {};
      const chartPoint: any = {
        ...previousPoint,
        time: new Date(data.timestamp).toLocaleTimeString(),
        timestamp: new Date(data.timestamp).getTime(),
      };

      Object.keys(data.values || {}).forEach((key) => {
        const rawValue = data.values[key];
        chartPoint[`${data.plcId}_${key}`] = typeof rawValue === 'number'
          ? parseFloat(rawValue.toFixed(2))
          : typeof rawValue === 'boolean'
            ? (rawValue ? 1 : 0)
            : chartPoint[`${data.plcId}_${key}`] ?? null;
      });

      // Keep enough history for the 15-minute correlation window.
      const newBuffer = [...historicalBuffer.current, chartPoint];
      if (newBuffer.length > MAX_HISTORY_POINTS) {
        historicalBuffer.current = newBuffer.slice(-MAX_HISTORY_POINTS);
      } else {
        historicalBuffer.current = newBuffer;
      }
    };

    const bridgeStatusHandler = (status: any) => {
      console.log('Bridge status:', status);
      const statusPlcs = Array.isArray(status?.plcs) ? status.plcs : [];

      setPlcs((previous) => {
        if (!Array.isArray(previous) || previous.length === 0) {
          return statusPlcs;
        }

        const merged = previous.map((plc) => {
          const plcId = plc.id || plc.plcId;
          const runtime = statusPlcs.find((candidate: any) => (candidate.plcId || candidate.id) === plcId);
          return runtime ? { ...plc, ...runtime, id: plcId } : plc;
        });

        const existingIds = new Set(merged.map((plc) => plc.id || plc.plcId));
        const extras = statusPlcs.filter((candidate: any) => !existingIds.has(candidate.plcId || candidate.id));
        return [...merged, ...extras];
      });
    };

    const plcConnectedHandler = (data: any) => {
      if (data?.connectionType === 'energy-meter' || data?.connectionType === 'scanner') return;
      console.log('PLC connected:', data);
      setPlcConnectionStatus((prev) => ({
        ...prev,
        [data.plcId]: { connected: true, reconnecting: false, error: null },
      }));
      toast.success(`${data.plcName} connected`, { id: `plc-status-${data.plcId}`, duration: 3000 });
    };

    const plcConnectionResultHandler = (data: any) => {
      if (data?.connected) {
        setPlcConnectionStatus((prev) => ({
          ...prev,
          [data.plcId]: { connected: true, reconnecting: false, error: null },
        }));
        return;
      }
      if (isSimulationMode) return; // real-hardware failure — irrelevant for a simulated client

      const plc = plcs.find((candidate) => candidate.id === data?.plcId);
      const plcName = plc?.name || 'PLC';
      const errorMessage = data?.error || 'Connection failed';

      setPlcConnectionStatus((prev) => ({
        ...prev,
        [data.plcId]: { connected: false, reconnecting: false, error: errorMessage },
      }));

      toast.error(`${plcName} connection failed`, {
        id: `plc-status-${data.plcId}`,
        duration: 5000,
        description: errorMessage,
      });
    };

    const plcErrorHandler = (data: any) => {
      if (data?.connectionType === 'energy-meter' || data?.connectionType === 'scanner') return;
      if (isSimulationMode) return; // real-hardware failure — irrelevant for a simulated client
      console.error('PLC error:', data);
      setPlcConnectionStatus((prev) => ({
        ...prev,
        [data.plcId]: { connected: false, reconnecting: false, error: data.error || 'Connection failed' },
      }));
      toast.error(`${data.plcName} disconnected`, { id: `plc-status-${data.plcId}`, duration: 3000 });
    };

    const plcStatusUpdateHandler = (data: any) => {
      if (data?.connectionType === 'energy-meter' || data?.connectionType === 'scanner') return;
      if (isSimulationMode && !data?.connected) return; // real-hardware failure — irrelevant for a simulated client
      lastRealtimePacketAt.current = Date.now();
      console.log('PLC status update:', data);
      setPlcConnectionStatus((prev) => {
        const prevStatus = prev[data.plcId];
        const wasConnected = prevStatus?.connected;
        const wasReconnecting = prevStatus?.reconnecting;
        const isConnected = !!data.connected;
        const isReconnecting = !!data.reconnecting && !data.connected;
        // Every status toast for a given PLC shares this same id, so sonner replaces
        // the existing notification in place instead of stacking a new one — this is
        // what was showing as multiple overlapping "reconnecting..." toasts before.
        const toastId = `plc-status-${data.plcId}`;

        // Only toast on actual transitions — suppress keep-alive broadcasts
        if (isConnected && wasConnected === false) {
          toast.success(`${data.plcName} reconnected`, { id: toastId, duration: 3000 });
        } else if (!isConnected && isReconnecting && !wasReconnecting) {
          toast.warning(`${data.plcName} reconnecting...`, {
            id: toastId,
            duration: 2500,
            description: data.error || 'Connection lost',
          });
        } else if (!isConnected && !isReconnecting && wasConnected === true) {
          toast.error(`${data.plcName} disconnected - auto-reconnect active`, {
            id: toastId,
            duration: 5000,
            description: data.error || 'Connection lost',
          });
        }

        // Skip state update if nothing changed
        if (wasConnected === isConnected && wasReconnecting === isReconnecting) return prev;

        return {
          ...prev,
          [data.plcId]: {
            connected: isConnected,
            reconnecting: isReconnecting,
            error: data.error || null,
          },
        };
      });
    };

    const errorHandler = (error: any) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
      toast.error('Connection error - attempting to reconnect...');
    };

    const disconnectedHandler = () => {
      setWsConnected(false);
      toast.error('Disconnected from real-time data');
    };

    ws.on('connected', connectedHandler);
    ws.on('plc_live_data', liveDataHandler);
    ws.on('bridge_status', bridgeStatusHandler);
    ws.on('plc_connected', plcConnectedHandler);
    ws.on('plc_connection_result', plcConnectionResultHandler);
    ws.on('plc_error', plcErrorHandler);
    ws.on('plc_status_update', plcStatusUpdateHandler);
    ws.on('error', errorHandler);
    ws.on('disconnected', disconnectedHandler);

    ws.connect();
    if (ws.isConnected()) {
      connectedHandler();
    }

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      ws.off('connected', connectedHandler);
      ws.off('plc_live_data', liveDataHandler);
      ws.off('bridge_status', bridgeStatusHandler);
      ws.off('plc_connected', plcConnectedHandler);
      ws.off('plc_connection_result', plcConnectionResultHandler);
      ws.off('plc_error', plcErrorHandler);
      ws.off('plc_status_update', plcStatusUpdateHandler);
      ws.off('error', errorHandler);
      ws.off('disconnected', disconnectedHandler);
    };
  }, [clientId, token, pollingIntervalMs, realtimeControlAllowed]);

  useEffect(() => {
    const plcIds = (Array.isArray(plcs) ? plcs : [])
      .map((plc: any) => String(plc?.id || plc?.plcId || '').trim())
      .filter(Boolean)
      .sort();
    const loadKey = plcIds.join('|');

    if (!loadKey) {
      return;
    }

    let isMounted = true;
    const loadSections = async () => {
      const entries = await Promise.all(plcIds.map(async (plcId) => {
        try {
          const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/sections`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            return [plcId, []] as const;
          }
          const sections = Array.isArray(payload) ? payload : (Array.isArray(payload?.sections) ? payload.sections : []);
          return [plcId, sections] as const;
        } catch {
          return [plcId, []] as const;
        }
      }));

      if (!isMounted) {
        return;
      }

      setSectionsByPlc((prev) => {
        const next: Record<string, any[]> = { ...prev };
        entries.forEach(([plcId, sections]) => {
          next[plcId] = sections;
        });
        return next;
      });
    };

    loadSections();

    return () => {
      isMounted = false;
    };
  }, [plcs, clientId, token]);

  useEffect(() => {
    if (realtimeControlAllowed !== false) {
      return () => {};
    }

    let isMounted = true;
    let pollTimer: NodeJS.Timeout | null = null;

    const updateSnapshotFromDatabase = async () => {
      try {
        const recentRealtimeWindowMs = Math.max(15000, effectivePollingIntervalMs * 3);
        const hasRecentRealtime =
          lastRealtimePacketAt.current > 0 &&
          (Date.now() - lastRealtimePacketAt.current) <= recentRealtimeWindowMs;

        // In VPS/SSE mode we can receive real-time packets even when realtime control is disabled.
        // If recent live packets exist, skip DB snapshot writes to avoid rolling values backward.
        if (hasRecentRealtime) {
          setLoading(false);
          return;
        }

        const plcResponse = await fetch(`${API_BASE}/api/clients/${clientId}/plcs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const plcPayload = await plcResponse.json();
        if (!plcResponse.ok) {
          throw new Error(plcPayload?.error || 'Failed to load PLC configurations');
        }

        const clientPlcs = (Array.isArray(plcPayload) ? plcPayload : plcPayload?.plcs || []).filter((p: any) => p.connectionType !== 'energy-meter' && p.connectionType !== 'scanner');
        if (!isMounted) return;
        // Only update plcs if structure changed — avoids unnecessary groupedDataByPlc recalculations
        setPlcs((prev) => {
          const prevSig = prev.map((p: any) => `${p.id || p.plcId}:${(p.tags || []).length}`).sort().join('|');
          const newSig = clientPlcs.map((p: any) => `${p.id || p.plcId}:${(p.tags || []).length}`).sort().join('|');
          return prevSig === newSig ? prev : clientPlcs;
        });

        const latestByKey = new Map<string, any>();
        const runtimeStatuses = await adminApi.getDeviceRuntimeStatus(token, clientId).catch(() => []);
        const runtimeStatusByPlcId = new Map<string, any>(
          (Array.isArray(runtimeStatuses) ? runtimeStatuses : []).map((status: any) => [String(status.plcId), status])
        );

        if (historicalBuffer.current.length === 0) {
          // Full history fetch only on first load — seeds the charts with recent history.
          // Every later poll uses the lightweight latest-values endpoint below instead, so we
          // don't re-scan/sort the entire historical_data table on every cycle.
          const historyResult = await adminApi.getHistoricalData(token, clientId, {
            limit: 1000,
            offset: 0,
          });

          const rows = Array.isArray(historyResult?.data) ? historyResult.data : [];

          if (rows.length > 0) {
            const bucketMap = new Map<number, any>();
            for (const row of rows) {
              const rowTs = row?.timestamp ?? row?.created_at ?? row?.createdAt;
              if (!rowTs) continue;
              const tsMs = new Date(rowTs).getTime();
              if (!Number.isFinite(tsMs)) continue;
              const rowPlcId = String(row?.plc_id ?? row?.plcId ?? '').trim();
              const rowTagName = String(row?.tag_name ?? row?.tagName ?? '').trim();
              if (!rowPlcId || !rowTagName) continue;
              const bucket = Math.floor(tsMs / 30000) * 30000;
              if (!bucketMap.has(bucket)) bucketMap.set(bucket, { time: new Date(bucket).toLocaleTimeString(), timestamp: bucket });
              const pt = bucketMap.get(bucket);
              const parsed = Number(row.value);
              const dk = `${rowPlcId}_${rowTagName}`;
              if (pt[dk] === undefined && Number.isFinite(parsed)) pt[dk] = parseFloat(parsed.toFixed(2));
            }
            const seedPoints = Array.from(bucketMap.values()).sort((a, b) => a.timestamp - b.timestamp).slice(-MAX_HISTORY_POINTS);
            if (seedPoints.length > 0) {
              historicalBuffer.current = seedPoints;
              setHistoricalData([...seedPoints]);
            }
          }

          for (const row of rows) {
            const rowTagId = row?.tag_id ?? row?.tagId;
            const rowPlcId = row?.plc_id ?? row?.plcId;
            const rowPlcName = String(row?.plc_name ?? row?.plcName ?? '').trim();
            const rowTagName = String(row?.tag_name ?? row?.tagName ?? '').trim();

            const keys: string[] = [];
            if (rowTagId !== undefined && rowTagId !== null && String(rowTagId).trim()) {
              keys.push(`tag:${String(rowTagId).trim()}`);
            }
            if (rowPlcId !== undefined && rowPlcId !== null && String(rowPlcId).trim() && rowTagName) {
              keys.push(`plcTag:${String(rowPlcId).trim()}::${rowTagName}`);
            }
            if (rowPlcName && rowTagName) {
              keys.push(`plcNameTag:${rowPlcName}::${rowTagName}`);
            }

            for (const key of keys) {
              if (!latestByKey.has(key)) {
                latestByKey.set(key, row);
              }
            }
          }
        } else {
          const latestResult = await adminApi.getPlcLatestValues(token, clientId).catch(() => null);
          const latestTags = Array.isArray(latestResult?.tags) ? latestResult.tags : [];

          for (const tag of latestTags) {
            if (tag?.value === null || tag?.value === undefined) continue;

            const row = {
              value: tag.value,
              timestamp: tag.timestamp,
              plc_id: tag.plcId,
              plc_name: tag.plcName,
              tag_name: tag.name,
            };

            const tagName = String(tag?.name || '').trim();
            const plcName = String(tag?.plcName || '').trim();

            const keys: string[] = [];
            if (tag?.id !== undefined && tag?.id !== null && String(tag.id).trim()) {
              keys.push(`tag:${String(tag.id).trim()}`);
            }
            if (tag?.plcId !== undefined && tag?.plcId !== null && String(tag.plcId).trim() && tagName) {
              keys.push(`plcTag:${String(tag.plcId).trim()}::${tagName}`);
            }
            if (plcName && tagName) {
              keys.push(`plcNameTag:${plcName}::${tagName}`);
            }

            for (const key of keys) {
              latestByKey.set(key, row);
            }
          }
        }

        const nowMs = Date.now();
        const freshnessThresholdMs = syncFreshnessThresholdMs;
        const nextLiveData: any[] = [];
        const nextStatus: Record<string, PlcRuntimeStatus> = {};

        for (const plc of clientPlcs) {
          const plcId = plc.id || plc.plcId;
          const plcName = plc.name || plc.plcName;
          const tags = Array.isArray(plc.tags) ? plc.tags : [];
          if (!plcId || !plcName || tags.length === 0) continue;

          const values: Record<string, any> = {};
          let latestTimestampMs = 0;

          for (const tag of tags) {
            const tagName = String(tag?.name || '').trim();
            if (!tagName) continue;

            const tagId = tag?.id ? String(tag.id).trim() : '';
            const lookupKeys = [
              tagId ? `tag:${tagId}` : '',
              `plcTag:${String(plcId)}::${tagName}`,
              plcName ? `plcNameTag:${String(plcName)}::${tagName}` : '',
            ].filter(Boolean);

            let latest: any = null;
            for (const lookupKey of lookupKeys) {
              const candidate = latestByKey.get(lookupKey);
              if (candidate) {
                latest = candidate;
                break;
              }
            }
            if (!latest) {
              values[tagName] = null;
              continue;
            }

            const parsed = Number(latest.value);
            values[tagName] = Number.isFinite(parsed) ? parsed : latest.value;

            const rowTimestamp = latest?.timestamp ?? latest?.created_at ?? latest?.createdAt;
            const tsMs = new Date(rowTimestamp).getTime();
            if (Number.isFinite(tsMs)) {
              latestTimestampMs = Math.max(latestTimestampMs, tsMs);
            }
          }

          const runtimeStatus = runtimeStatusByPlcId.get(String(plcId));
          const connectedByData = latestTimestampMs > 0 && (nowMs - latestTimestampMs) <= freshnessThresholdMs;
          const runtimeConnected = !!runtimeStatus?.connected;
          // A known timestamp is hard evidence — if it's stale, the PLC has stopped
          // sending fresh data, and a stuck "connected" flag from an old websocket
          // event (which never resets on a silent poll stall, only on an explicit
          // disconnect event) must not override that. Only fall back to the runtime
          // flag when we have no timestamp at all yet.
          const connected = latestTimestampMs > 0 ? connectedByData : runtimeConnected;
          nextStatus[String(plcId)] = {
            connected,
            reconnecting: !connected && !!runtimeStatus?.reconnecting,
            error: connected ? null : (runtimeStatus?.error || 'No fresh PLC data yet'),
          };

          nextLiveData.push({
            plcId: String(plcId),
            plcName: String(plcName),
            timestamp: latestTimestampMs > 0 ? new Date(latestTimestampMs).toISOString() : '',
            values,
            connectionType: getPlcConfigValue(plc, 'connectionType', 'connection_type', 'tcp'),
            ipAddress: getPlcConfigValue(plc, 'ipAddress', 'ip_address', ''),
            serialPort: getPlcConfigValue(plc, 'serialPort', 'serial_port', ''),
          });
        }

        if (!isMounted) return;

        setPlcConnectionStatus(nextStatus);
        setLiveData(nextLiveData);

        if (nextLiveData.length > 0) {
          const chartPoint: any = {
            time: new Date().toLocaleTimeString(),
            timestamp: Date.now(),
          };
          nextLiveData.forEach((plc) => {
            Object.entries(plc.values || {}).forEach(([key, rawValue]) => {
              chartPoint[`${plc.plcId}_${key}`] = typeof rawValue === 'number'
                ? Number.parseFloat(rawValue.toFixed(2))
                : typeof rawValue === 'boolean'
                  ? (rawValue ? 1 : 0)
                  : null;
            });
          });

          historicalBuffer.current = [...historicalBuffer.current, chartPoint].slice(-MAX_HISTORY_POINTS);
          setHistoricalData([...historicalBuffer.current]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to load PLC sync snapshot:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    updateSnapshotFromDatabase();

    const intervalMs = effectivePollingIntervalMs;
    pollTimer = setInterval(updateSnapshotFromDatabase, intervalMs);

    return () => {
      isMounted = false;
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [realtimeControlAllowed, clientId, token, effectivePollingIntervalMs, syncFreshnessThresholdMs]);

  useEffect(() => {
    if (liveData.length === 0) return;
    if (selectedPlcId && liveData.some((plc) => plc.plcId === selectedPlcId)) return;
    setSelectedPlcId(liveData[0].plcId);
  }, [liveData, selectedPlcId]);

  useEffect(() => {
    if (!selectedPlcId) return;
    setSelectedSectionByPlc((prev) => {
      if (prev[selectedPlcId]) return prev;
      return {
        ...prev,
        [selectedPlcId]: 'all',
      };
    });
  }, [selectedPlcId]);


  const handleExportPDF = async () => {
    if (!permissions?.livePlcData?.download) {
      toast.error('You do not have permission to download PDFs');
      return;
    }

    toast.success('Generating PDF report...');
    
    // Always fetch the latest template from the API to ensure we have the updated version
    let clientTemplate: any = undefined;
    try {
      const latestTemplate = await adminApi.getPDFTemplate(token, clientId);
      if (latestTemplate?.success && latestTemplate?.template) {
        clientTemplate = latestTemplate.template;
      }
    } catch (error) {
      console.warn('Could not fetch latest PDF template:', error);
      // Fall back to the template passed via props
      clientTemplate = client?.pdfTemplates?.plc || undefined;
    }
    
    const clientName = client?.name || 'Client';
    
    // Create default template if none exists
    if (!clientTemplate?.advancedTemplate) {
      // Group tags by PLC for professional multi-level headers
      const reportSections: any[] = plcs.map(plc => {
        const plcId = String(plc.id || plc.plcId);
        const plcTags = (plc.tags || []).map((tag: any) => ({
          id: `${plcId}_${tag.name}`,
          label: tag.name,
          dataKey: `${plcId}_${tag.name}`,
          format: 'number' as const
        }));

        return {
          id: plcId,
          title: plc.name || 'PLC Data',
          columns: plcTags
        };
      }).filter(s => s.columns.length > 0);

      clientTemplate = {
        advancedTemplate: {
          reportTitle: 'LIVE PLC DATA LOG REPORT',
          reportSubtitle: clientName,
          companyFields: [
            { label: 'COMPANY NAME', value: clientName },
            { label: 'USER NAME', value: '{userName}' }
          ],
          showDateRange: true,
          dateRangeLabel: 'Report Period',
          sections: reportSections,
          showCheckedBy: true,
          showVerifiedBy: true,
          showGeneratedBy: true,
          footerText: `Generated on {date} {time}`,
          primaryColor: '#3b82f6',
          borderStyle: 'single' as const,
          fontSize: 'medium' as const
        }
      };
    }
    
    const startDate = historicalData.length > 0 ? new Date(historicalData[0].timestamp) : new Date();
    const endDate = historicalData.length > 0 ? new Date(historicalData[historicalData.length - 1].timestamp) : new Date();
    await generateCustomPLCReport(
      liveData,
      historicalData,
      clientTemplate.advancedTemplate,
      clientName,
      startDate,
      endDate
    );

    await adminApi.logClientAuditEvent(token, clientId, {
      eventType: 'pdf_download',
      description: 'Downloaded live PLC data PDF report',
      metadata: {
        report: 'live_plc_data',
        fileName: `live_plc_data_${new Date().toISOString().split('T')[0]}.pdf`,
        liveRecords: liveData.length,
        historicalPoints: historicalData.length,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
      },
    }).catch((error) => {
      console.warn('Failed to record live PLC PDF audit event:', error);
    });
    toast.success('PDF report downloaded');
  };

  const getTrendIcon = (value: number, previousValue: number) => {
    if (value > previousValue) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (value < previousValue) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const getPlcStatus = (plcId: string, timestamp?: string) => {
    const thresholdMs = realtimeControlAllowed === false
      ? syncFreshnessThresholdMs
      : realtimeFreshnessThresholdMs;
    const connectedByTimestamp = !!(timestamp && (Date.now() - new Date(timestamp).getTime()) < thresholdMs);

    const runtime = plcConnectionStatus[plcId];
    if (runtime) {
      // Same principle as the sync-mode status computation above: a present
      // timestamp is hard evidence, so it decides. A sticky runtime.connected
      // flag only fills in when there's no timestamp to check yet (e.g. right
      // after opening the page, before the first data point has arrived).
      const connected = timestamp ? connectedByTimestamp : !!runtime.connected;
      return {
        connected,
        reconnecting: !connected && !!runtime.reconnecting,
        error: connected ? null : (runtime.error || null),
      };
    }

    return {
      connected: connectedByTimestamp,
      reconnecting: false,
      error: null,
    };
  };

  const exportLiveData = async (format: 'csv') => {
    if (!permissions?.livePlcData?.download) {
      toast.error('You do not have permission to download data');
      return;
    }

    const selected = liveData.find((plc) => plc.plcId === selectedPlcId);
    if (!selected) {
      toast.error('No PLC selected');
      return;
    }

    try {
      const blob = await adminApi.downloadHistoricalData(token, clientId, format, {
        plcId: selected.plcId,
      });

      if (!blob || blob.size === 0) {
        toast.error('No saved data available to download');
        return;
      }

      const datePart = new Date().toISOString().split('T')[0];
      const baseFileName = `${selected.plcName || 'plc'}_historical_data_${datePart}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download saved data');
    }
  };


  const parseTagKey = (key: string) => {
    const match = String(key || '').match(/^(.*)_([A-Za-z]?\d+(?:\.\d+)?)$/);
    if (!match) {
      return {
        name: key,
        address: '',
      };
    }

    return {
      name: match[1] || key,
      address: match[2] || '',
    };
  };

  // Boolean/M-bit (coil) tags are for on/off equipment status (valves, pumps, motors
  // in the HMI Builder etc.) — this view is for numeric process values, so they're
  // deliberately left out here rather than shown as a bare "0"/"1".
  const isBooleanTag = (tag: any) => {
    const dataType = String(tag?.dataType ?? tag?.data_type ?? '').trim().toLowerCase();
    return dataType === 'bool' || dataType === 'boolean';
  };

  const getTagMeta = (plcId: string, key: string) => {
    const plc = plcs.find((p) => p.id === plcId || p.plcId === plcId);
    const tags = Array.isArray(plc?.tags) ? plc.tags : [];
    if (tags.length === 0) return null;

    const keyNormalized = String(key || '').trim();
    const exactMatch = tags.find((t: any) => String(t?.name || '').trim() === keyNormalized);
    if (exactMatch) {
      return exactMatch;
    }

    const { name, address } = parseTagKey(keyNormalized);
    return tags.find((t: any) => {
      if (String(t?.name || '').trim() !== String(name || '').trim()) return false;
      if (!address) return true;
      return String(t?.address ?? '').trim() === String(address).trim();
    }) || null;
  };

  // Memoize grouped values to avoid expensive re-calculations on every render
  const groupedDataByPlc = useMemo(() => {
    const result: Record<string, any> = {};
    
    liveData.forEach(plc => {
      const plcId = String(plc?.plcId || plc?.id || '');
      const sections = sectionsByPlc[plcId] || [];
      const groupedValues: Record<string, { key: string, value: any }[]> = { unassigned: [] };

      sections.forEach((section: any) => {
        const sectionId = String(section?.id || '');
        if (sectionId) {
          groupedValues[sectionId] = [];
        }
      });

      // Build a stable order index from the configured tags so cards never reorder on live updates
      const plcConfig = plcs.find((p: any) => p.id === plcId || p.plcId === plcId);
      const tagOrderMap = new Map<string, number>();
      (plcConfig?.tags || []).forEach((tag: any, idx: number) => {
        tagOrderMap.set(String(tag?.name || '').trim(), idx);
      });

      // Use configured tags as the stable source of truth so every tag always
      // has a card even when a live poll cycle omits it (read failure / no value).
      const configuredTags: any[] = plcConfig?.tags || [];
      const liveValues: Record<string, any> = plc?.values || {};

      // Collect tag names already rendered from config
      const configuredTagNames = new Set<string>();
      configuredTags.forEach((tag: any) => {
        const key = String(tag?.name || '').trim();
        if (!key) return;
        configuredTagNames.add(key);
        if (isBooleanTag(tag)) return; // Boolean/M-bit tags are excluded from this view
        const value = Object.prototype.hasOwnProperty.call(liveValues, key) ? liveValues[key] : null;
        const sectionIdRaw = tag?.sectionId ?? tag?.section_id ?? 'unassigned';
        const sectionId = String(sectionIdRaw || 'unassigned');
        if (!groupedValues[sectionId]) {
          groupedValues.unassigned.push({ key, value });
        } else {
          groupedValues[sectionId].push({ key, value });
        }
      });

      // Also include any live value keys not in the configured list (e.g. dynamic tags).
      // Skip keys that don't match ANY current tag — these are stale broadcasts from a
      // bridge that hasn't yet picked up a rename/address change (it briefly keeps
      // reporting under the old tag name), not real dynamic tags, so showing them as
      // phantom cards would just be confusing leftover noise.
      Object.entries(liveValues).forEach(([key, value]) => {
        if (configuredTagNames.has(key)) return; // already added above
        const tagMeta = getTagMeta(plcId, key);
        if (!tagMeta) return; // no current tag matches this key — stale/unrecognized, drop it
        if (isBooleanTag(tagMeta)) return; // Boolean/M-bit tags are excluded from this view
        const sectionIdRaw = tagMeta?.sectionId ?? tagMeta?.section_id ?? 'unassigned';
        const sectionId = String(sectionIdRaw || 'unassigned');
        if (!groupedValues[sectionId]) {
          groupedValues.unassigned.push({ key, value });
        } else {
          groupedValues[sectionId].push({ key, value });
        }
      });

      // Sort each section's tags by their configured order
      Object.keys(groupedValues).forEach(sid => {
        groupedValues[sid].sort((a, b) => {
          const ai = tagOrderMap.has(a.key) ? tagOrderMap.get(a.key)! : 9999;
          const bi = tagOrderMap.has(b.key) ? tagOrderMap.get(b.key)! : 9999;
          return ai - bi;
        });
      });

      result[plcId] = {
        plcId,
        sections,
        groupedValues,
      };
    });

    return result;
  }, [liveData, sectionsByPlc, plcs]);

  const visibleTagKeysByPlc = (plcId: string) => {
    const data = groupedDataByPlc[plcId];
    if (!data) return [];
    
    const { groupedValues } = data;
    const selectedSectionId = selectedSectionByPlc[plcId] || 'all';

    if (selectedSectionId === 'all') {
      const plc = liveData.find(p => p.plcId === plcId);
      return Object.keys(plc?.values || {});
    }

    return (groupedValues[selectedSectionId] || []).map((entry: any) => entry.key);
  };

  const isSyncMode = realtimeControlAllowed === false;
  const healthyLabel = isSyncMode ? 'Synced' : 'Connected';
  const unhealthyLabel = isSyncMode ? 'Stale' : 'Disconnected';

  if (!permissions?.livePlcData?.view) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">You do not have permission to view this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading live data...</p>
          </div>
        </div>
      ) : liveData.length === 0 ? (
        <Card className="p-12 text-center">
          <WifiOff className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-xl font-semibold mb-2">
            {realtimeControlAllowed === false ? 'No Fresh PLC Data Yet' : 'No PLC Connection'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {realtimeControlAllowed === false
              ? 'Bridge sync is enabled, but no recent PLC readings have arrived yet.'
              : 'Waiting for real PLC data from bridge server...'}
          </p>
          <ol className="text-muted-foreground space-y-2 mb-4 text-left max-w-md mx-auto">
            <li>1. Ensure bridge service is running on the client/gateway machine</li>
            <li>2. Check PLC tags are configured in Admin Panel PLC settings</li>
            <li>3. Wait up to 1 minute for bridge registration refresh after tag changes</li>
            <li>4. Verify PLC network connection and address values</li>
          </ol>
          <p className="text-xs text-muted-foreground">
            {realtimeControlAllowed === false
              ? '💡 Viewing latest synced data (no direct websocket control on this host)'
              : `💡 Bridge connects to: ${wsConnected ? '✅ Connected' : '❌ Disconnected'}`}
          </p>
        </Card>
      ) : (
        <>
          {/* PLC Status Summary */}
          <div className="bg-gradient-to-r from-white dark:from-slate-900 to-slate-50 dark:to-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap items-center justify-start gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">{healthyLabel}</span>
                  <span className="text-sm font-semibold text-emerald-400">
                    {liveData.filter((plc) => getPlcStatus(plc.plcId, plc.timestamp).connected).length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">{unhealthyLabel}</span>
                  <span className={`text-sm font-semibold ${isSyncMode ? 'text-amber-600 dark:text-amber-300' : 'text-red-600 dark:text-red-400'}`}>
                    {liveData.filter((plc) => !getPlcStatus(plc.plcId, plc.timestamp).connected).length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* PLC Selector - Professional Button Group */}
          <div className="bg-gradient-to-r from-white dark:from-slate-900 to-slate-50 dark:to-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">PLC Status Monitor</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isSyncMode ? 'Select a PLC to view latest synced data' : 'Select a PLC to monitor in real-time'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {liveData.map((plc, idx) => {
                const isSelected = plc.plcId === selectedPlcId;
                const status = getPlcStatus(plc.plcId, plc.timestamp);
                const isConnected = status.connected;
                const isReconnecting = !isSyncMode && !!status.reconnecting;
                const statusDotClass = isReconnecting
                  ? 'bg-amber-300'
                  : (isConnected ? (isSelected ? 'bg-white' : 'bg-emerald-500') : (isSyncMode ? 'bg-amber-400' : 'bg-red-400'));
                // Selected buttons are solid green; unselected ones follow the page theme.
                const statusTextClass = isSelected
                  ? (isReconnecting ? 'text-amber-200' : (isConnected ? 'text-emerald-100' : (isSyncMode ? 'text-amber-200' : 'text-red-200')))
                  : (isReconnecting ? 'text-amber-700 dark:text-amber-200' : (isConnected ? 'text-emerald-700 dark:text-emerald-300' : (isSyncMode ? 'text-amber-700 dark:text-amber-200' : 'text-red-600 dark:text-red-300')));
                const statusText = isReconnecting
                  ? 'Reconnecting...'
                  : (isConnected ? healthyLabel : unhealthyLabel);
                return (
                  <motion.button
                    key={plc.plcId}
                    onClick={() => setSelectedPlcId(plc.plcId)}
                    className={`relative w-full min-w-0 max-w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 sm:w-auto sm:px-6 ${
                      isSelected
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/50'
                        : `bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600`
                    }`}
                  >
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-2">
                        <span className="block max-w-[190px] truncate text-sm uppercase tracking-widest sm:max-w-[240px]">{plc.plcName}</span>
                        <motion.div
                          animate={isConnected && !isReconnecting ? { scale: [1, 1.2, 1] } : {}}
                          transition={{ repeat: Infinity, duration: 2 }}
                          className={`w-2 h-2 rounded-full ${statusDotClass}`}
                        />
                      </div>
                      <span className={`text-xs ${statusTextClass}`}>
                        {statusText}
                      </span>
                    </div>
                    {isSelected && (
                      <motion.div
                        layoutId="plc-underline"
                        className="absolute bottom-0 left-0 right-0 h-1 bg-white rounded-b-lg"
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Live Values Metrics Grouped by Section */}
          {liveData
            .filter((plc) => plc.plcId === selectedPlcId)
            .map((plc) => {
              const data = groupedDataByPlc[plc.plcId];
              if (!data) return null;
              const { plcId, sections, groupedValues } = data;
              const selectedSectionId = selectedSectionByPlc[plcId] || 'all';

              const availableSections = [
                { id: 'all', name: 'All Sections', count: Object.keys(plc.values || {}).length },
                ...sections.map((section: any) => {
                  const sectionId = String(section?.id || '');
                  return {
                    id: sectionId,
                    name: section?.name || 'Unnamed Section',
                    count: (groupedValues[sectionId] || []).length,
                  };
                }),
                {
                  id: 'unassigned',
                  name: 'General / Unassigned',
                  count: (groupedValues.unassigned || []).length,
                },
              ].filter((section, index, self) => self.findIndex((entry) => entry.id === section.id) === index);

              const sectionEntries: Array<[string, { key: string; value: any }[]]> = selectedSectionId === 'all'
                ? (Object.entries(groupedValues) as Array<[string, { key: string; value: any }[]]>)
                : [[selectedSectionId, groupedValues[selectedSectionId] || []]];

              return (
                <div key={`${plc.plcId}-metrics`} className="space-y-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/70 p-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Section Filter</p>
                      <p className="text-sm text-slate-700 dark:text-slate-300">Select the section you want to see now.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mt-3 sm:mt-0">
                      <select
                        value={selectedSectionId}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedSectionByPlc((prev) => ({
                            ...prev,
                            [plcId]: value,
                          }));
                        }}
                        className="h-10 w-full max-w-full rounded-md border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-400 sm:w-auto sm:min-w-[220px]"
                      >
                        {availableSections.map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.name} ({section.count})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {sectionEntries.map(([sectionId, tags]) => {
                    if (tags.length === 0) return null;
                    const sectionName = sectionId === 'unassigned' 
                      ? 'General / Unassigned' 
                      : (sections.find((s: any) => s.id === sectionId)?.name || 'Unknown Section');

                    // Group this section's tags by inferred measurement category (from
                    // each tag's real configured unit) so they render like the
                    // Temperature / Flow Rate / Pressure themed panels.
                    const byCategory = new Map<string, { key: string; value: any }[]>();
                    tags.forEach((t) => {
                      const tagMeta = getTagMeta(plc.plcId, t.key);
                      const label = categorizeUnit(tagMeta?.unit || '').label;
                      if (!byCategory.has(label)) byCategory.set(label, []);
                      byCategory.get(label)!.push(t);
                    });

                    return (
                      <div key={sectionId} className="space-y-6">
                        <div className="flex items-center gap-3">
                          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{sectionName}</h3>
                          <div className="h-[1px] flex-1 bg-slate-100 dark:bg-slate-700" />
                        </div>

                        {Array.from(byCategory.entries()).map(([categoryLabel, categoryTags]) => {
                          const category = categoryLabel === 'Other' ? DEFAULT_CATEGORY : (CATEGORY_RULES.find((r) => r.label === categoryLabel) || DEFAULT_CATEGORY);
                          const CategoryIcon = category.icon;
                          const expandKey = `${sectionId}::${categoryLabel}`;
                          const isExpanded = !!expandedCategories[expandKey];
                          const visibleTags = isExpanded ? categoryTags : categoryTags.slice(0, 5);
                          const firstUnit = (getTagMeta(plc.plcId, categoryTags[0]?.key)?.unit) || '';

                          return (
                            <div key={categoryLabel} className={`rounded-lg border border-slate-200 dark:border-slate-700 border-t-4 ${category.border} bg-white dark:bg-slate-900/60 overflow-hidden`}>
                              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                  <span className={`p-1.5 rounded-md ${category.iconWrap}`}>
                                    <CategoryIcon className="h-4 w-4" />
                                  </span>
                                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">{categoryLabel}</h4>
                                  {firstUnit && <span className="text-xs text-slate-500">{firstUnit}</span>}
                                </div>
                                {categoryTags.length > 5 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                                    onClick={() => setExpandedCategories((prev) => ({ ...prev, [expandKey]: !isExpanded }))}
                                  >
                                    {isExpanded ? <>Show Less <ChevronUp className="h-3 w-3 ml-1" /></> : <>View All ({categoryTags.length}) <ChevronDown className="h-3 w-3 ml-1" /></>}
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                                {visibleTags.map(({ key, value }) => {
                                  const tagMeta = getTagMeta(plc.plcId, key);
                                  const unit = tagMeta?.unit ? String(tagMeta.unit) : '';
                                  const minValue = Number.isFinite(Number(tagMeta?.minValue)) ? Number(tagMeta.minValue) : null;
                                  const maxValue = Number.isFinite(Number(tagMeta?.maxValue)) ? Number(tagMeta.maxValue) : null;
                                  const history = tagHistoryRef.current[`${plc.plcId}::${key}`] || [];
                                  const isBelowMin = smartAlertsEnabled && typeof value === 'number' && minValue !== null && value < minValue;
                                  const isAboveMax = smartAlertsEnabled && typeof value === 'number' && maxValue !== null && value > maxValue;
                                  const isOutOfRange = isBelowMin || isAboveMax;
                                  return (
                                    <div
                                      key={key}
                                      className={`bg-gradient-to-br from-white dark:from-slate-800 to-slate-50 dark:to-slate-900 rounded-lg p-4 border transition-all shadow-lg ${
                                        isOutOfRange ? 'border-red-500/70 ring-1 ring-red-500/40' : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">{key}</p>
                                          <p className={`text-2xl font-bold mt-1 ${isOutOfRange ? 'text-red-400' : 'text-slate-900 dark:text-white'}`}>
                                            {typeof value === 'number' ? value.toFixed(2) : (
                                              typeof value === 'boolean' ? (value ? '1' : '0') : (
                                                value === null || value === undefined || value === ''
                                                  ? <span className="text-base font-semibold text-slate-500">No value</span>
                                                  : value
                                              )
                                            )}
                                            {unit ? <span className="text-sm text-slate-500 dark:text-slate-400 ml-2">{unit}</span> : null}
                                          </p>
                                          <p className="text-xs text-slate-500 mt-1">Real time data</p>
                                        </div>
                                        <span className={`p-1.5 rounded-md shrink-0 ${category.iconWrap}`}>
                                          <CategoryIcon className="h-4 w-4" />
                                        </span>
                                      </div>
                                      {(minValue !== null || maxValue !== null) && (
                                        <p className={`text-[11px] mt-2 ${isOutOfRange ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
                                          Min {minValue ?? '—'}{unit} &nbsp; Max {maxValue ?? '—'}{unit}
                                          {isOutOfRange && (isBelowMin ? ' — below min' : ' — above max')}
                                        </p>
                                      )}
                                      <div className="mt-1">
                                        <MiniSparkline data={history} color={isOutOfRange ? '#ef4444' : category.accent} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {sectionEntries.every(([, tags]) => tags.length === 0) && (
                    <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/70">
                      <CardContent className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                        No tags available in the selected section.
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })}

          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCharts(!showCharts)}
              className="bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              {showCharts ? 'Hide Trend' : 'Show Trend'}
            </Button>
          </div>

          {/* One chart per measurement category — mixing e.g. a 0-2 flow rate with a
              0-500 pressure reading on a single shared axis made every smaller-scale
              tag look flat/invisible. Splitting by the same category grouping used
              above keeps each chart's axis meaningful for what's actually on it.
              The range/scroll-back controls below apply the same way to every one
              of these category charts — one shared window, not a per-chart toggle. */}
          {showCharts && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/70 p-3 mb-4">
              <div className="flex items-center gap-1">
                {(['live', 'day', 'week', 'month'] as TrendRange[]).map((r) => (
                  <Button
                    key={r}
                    size="sm"
                    variant={trendRange === r ? 'default' : 'outline'}
                    className={trendRange === r ? '' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'}
                    onClick={() => setTrendRange(r)}
                  >
                    {r === 'live' ? 'Live' : r.charAt(0).toUpperCase() + r.slice(1)}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {trendRange !== 'live' && (
                  <Button variant="ghost" size="icon" onClick={() => setTrendOffset((o) => o + 1)} aria-label="Previous period">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                <span className="text-sm text-slate-700 dark:text-slate-300 min-w-[140px] text-center">
                  {historicalTrendLoading ? 'Loading...' : trendWindowLabel}
                </span>
                {trendRange !== 'live' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTrendOffset((o) => Math.max(0, o - 1))}
                    disabled={trendOffset === 0}
                    aria-label="Next period"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {showCharts && activeTrendData.length > 0 &&
            liveData
              .filter((plc) => plc.plcId === selectedPlcId)
              .map((plc) => {
                const keys = visibleTagKeysByPlc(plc.plcId);
                return (
                  <Card key={`${plc.plcId}-trend`} className="border-0 bg-white dark:bg-slate-900 shadow-xl overflow-hidden">
                    <CardHeader className="border-b border-slate-200 dark:border-slate-800">
                      <CardTitle className="text-slate-900 dark:text-white">Combined Trend</CardTitle>
                      <CardDescription className="text-slate-500 dark:text-slate-400">Historical trend for all tags</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="h-[450px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={activeTrendData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                            <XAxis
                              dataKey="time"
                              stroke="#64748b"
                              tick={{ fontSize: 12 }}
                              minTickGap={30}
                              interval="preserveStartEnd"
                              tickLine={false}
                              axisLine={false}
                            />
                            <YAxis stroke="#64748b" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                            <Tooltip
                              contentStyle={{ backgroundColor: isDark ? '#0f172a' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: '12px' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: 10, fontSize: 12 }} verticalAlign="bottom" height={64} />
                            {keys.map((key: string, idx: number) => (
                              <Line
                                key={key}
                                type="monotone"
                                dataKey={`${plc.plcId}_${key}`}
                                stroke={CHART_PALETTE[idx % CHART_PALETTE.length]}
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                                isAnimationActive={false}
                                name={key}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
        </>
      )}
    </div>
  );
}

// Live PLC Data entry point. Clients without VFD drives or controllers get exactly
// the PLC tag view above, unchanged. When the admin has configured VFD drives and/or
// process controllers for this client, a "PLC Tags | VFD Drives | Controllers"
// switch appears on top, showing only the sections that exist.
export function LivePLCData(props: LivePLCDataProps) {
  const { clientId, token, permissions } = props;
  const [vfds, setVfds] = useState<VfdDrive[]>([]);
  const [controllers, setControllers] = useState<ProcessController[]>([]);
  const [view, setView] = useState<'plc' | 'vfd' | 'ctl'>('plc');
  // null until known; a client without PLCs gets no "PLC Tags" tab at all.
  const [plcCount, setPlcCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [vfdList, ctlList, plcs] = await Promise.all([
        vfdApi.listForClient(token, clientId).catch(() => [] as VfdDrive[]),
        controllerApi.listForClient(token, clientId).catch(() => [] as ProcessController[]),
        fetch(`${API_BASE}/api/clients/${clientId}/plcs`, { headers: { Authorization: `Bearer ${token}` } })
          .then(async (res) => {
            if (!res.ok) return null;
            const body = await res.json().catch(() => ({}));
            return Array.isArray(body) ? body.length : (Array.isArray(body?.plcs) ? body.plcs.length : null);
          })
          .catch(() => null),
      ]);
      if (cancelled) return;
      setVfds(vfdList);
      setControllers(ctlList);
      setPlcCount(plcs);
      // A client with only drives/controllers opens on them instead of an empty PLC view.
      if (plcs === 0 && (vfdList.length > 0 || ctlList.length > 0)) setView(vfdList.length > 0 ? 'vfd' : 'ctl');
    })();
    return () => { cancelled = true; };
  }, [clientId, token]);

  const canView = !!permissions?.livePlcData?.view;
  const hasVfds = vfds.length > 0 && canView;
  const hasControllers = controllers.length > 0 && canView;
  const hasExtras = hasVfds || hasControllers;
  const hasPlcs = plcCount !== 0; // unknown counts as yes, so a slow API never hides real PLCs
  const tabs = [
    { id: 'plc' as const, label: 'PLC Tags', icon: Activity, show: hasPlcs },
    { id: 'vfd' as const, label: `VFD Drives (${vfds.length})`, icon: Gauge, show: hasVfds },
    { id: 'ctl' as const, label: `Controllers (${controllers.length})`, icon: Thermometer, show: hasControllers },
  ].filter((t) => t.show);

  return (
    <div className="space-y-6">
      {hasExtras && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="tablist" aria-label="Live data source" className="inline-flex flex-wrap rounded-lg border border-border bg-muted/40 p-1">
            {tabs.map((tab) => {
              const active = view === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setView(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Kept mounted while hidden so switching tabs doesn't reset PLC live state. */}
      {hasPlcs && (
        <div className={hasExtras && view !== 'plc' ? 'hidden' : undefined}>
          <LivePLCTagsView {...props} />
        </div>
      )}
      {hasVfds && view === 'vfd' && (
        <VFDDrivesPanel clientId={clientId} token={token} initialDrives={vfds} />
      )}
      {hasControllers && view === 'ctl' && (
        <ControllersPanel clientId={clientId} token={token} initialControllers={controllers} />
      )}
    </div>
  );
}
